import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Download, Loader2, Printer } from "lucide-react";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import { Badge } from "src/components/ui/Badge";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import FilterSelect from "src/components/ui/FilterSelect";
import PurchaseInvoiceDocument from "src/components/invoice/PurchaseInvoiceDocument";
import purchaseOrderApis from "src/api/purchaseOrder";
import { currencyFormatter, formatVariantLabel } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { formatAddress } from "src/utils/formatAddress";
import { generatePDF } from "src/utils/pdfGenerator";
import { printElement } from "src/utils/printElement";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import * as purchaseActions from "src/redux/slices/purchaseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";

const statusColor = (status: string) =>
  status === "received" ? "green" : status === "cancelled" ? "red" : "yellow";

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const resolveVariant = (product: any, variantId: any) => {
  if (!variantId || !product?.variants) return null;
  return (
    product.variants.find((v: any) => String(v._id) === String(variantId)) ||
    null
  );
};

const PurchaseOrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const invoiceRef = useRef<HTMLDivElement>(null);

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const tenant = user?.tenantDetails;
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { receive } = useAppSelector((state) => state.purchase);
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderId) getOrderDetails(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const getOrderDetails = async (id: string) => {
    try {
      setLoading(true);
      const res = await purchaseOrderApis.getDetails(id);
      setOrder(res?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (receive.data) {
      toast.success("Goods received successfully");
      dispatch(purchaseActions.resetReceiveGoods());
      if (orderId) getOrderDetails(orderId);
    } else if (receive.error) {
      toast.error(`Error: ${receive.error}`);
      dispatch(purchaseActions.resetReceiveGoods());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receive]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await generatePDF(invoiceRef, `purchase-${order.purchaseOrderId}.pdf`);
    } catch (error) {
      toast.error("Failed to generate purchase order PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => printElement();

  const handleReceiveGoods = () => {
    if (!orderId || !warehouseId) return;
    dispatch(purchaseActions.receiveGoodsRequest({ id: orderId, warehouseId }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex h-96 items-center justify-center">
          <Loader2 className="animate-spin text-primary-600" size={28} />
          <span className="ml-3 text-gray-700 dark:text-gray-300">Loading order...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          Order not found or deleted.
        </div>
      </DashboardLayout>
    );
  }

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  return (
    <DashboardLayout
      meta={{
        title: `Order ${order.purchaseOrderId} | Invyntra`,
        description: "View purchase order details and receive goods.",
      }}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Breadcrumb
            data={[
              { label: "Purchases", path: "/app/purchase/list" },
              { label: `#${order.purchaseOrderId}` },
            ]}
          />
          <div className="flex items-center gap-2">
            <Button onClick={handleDownload} isLoading={downloading} disabled={downloading}>
              <Download size={16} className="mr-1.5" />
              Download PDF
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer size={16} className="mr-1.5" />
              Print
            </Button>
            <Button variant="outline" onClick={() => navigate("/app/purchase/list")}>
              Back to purchases
            </Button>
          </div>
        </div>

        <Card>
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-4 dark:border-gray-700">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {tenant?.name || "Your Business"}
              </h1>
              {tenant?.gst && (
                <p className="text-sm text-gray-500 dark:text-gray-400">GSTIN: {tenant.gst}</p>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 p-3 text-right dark:border-gray-700">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Order No</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{order.purchaseOrderId}</p>
              <p className="mt-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Order Date</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {formatDateTime(order.orderDate || order.createdAt, "DD MMM YYYY")}
              </p>
              <div className="mt-2">
                <Badge color={statusColor(order.orderStatus)} text={capitalize(order.orderStatus)} />
              </div>
            </div>
          </div>

          <div className="border-b border-gray-100 py-4 dark:border-gray-700">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Bill From (Supplier)
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100">{order.supplierId?.name}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {formatAddress(order.supplierId) || "-"}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {order.supplierId?.phone || "-"} · {order.supplierId?.email || "-"}
            </p>
          </div>

          <div className="overflow-x-auto py-4">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Product</th>
                  <th className="py-2 pr-2">SKU</th>
                  <th className="py-2 pr-2">HSN</th>
                  <th className="py-2 pr-2 text-right">Qty</th>
                  <th className="py-2 pr-2 text-right">Price</th>
                  <th className="py-2 pr-2 text-right">Taxable</th>
                  <th className="py-2 pr-2 text-right">Tax</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(order.products || []).map((item: any, idx: number) => {
                  const product = item.productId;
                  const variant = resolveVariant(product, item.variantId);
                  const tax = (item.totalPrice * (item.taxPercent || 0)) / 100;
                  return (
                    <tr key={idx} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-2 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {product?.name}
                        </div>
                        {variant && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatVariantLabel(variant.optionValues)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-gray-600 dark:text-gray-400">
                        {variant?.sku || product?.sku || "-"}
                      </td>
                      <td className="py-2 pr-2 text-gray-600 dark:text-gray-400">
                        {product?.hsnCode || "-"}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-700 dark:text-gray-300">
                        {item.quantity}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-700 dark:text-gray-300">
                        {currencyFormatter(item.buyingPrice.toFixed(2))}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-700 dark:text-gray-300">
                        {currencyFormatter(item.totalPrice.toFixed(2))}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-700 dark:text-gray-300">
                        {currencyFormatter(tax.toFixed(2))}
                      </td>
                      <td className="py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        {currencyFormatter((item.totalPrice + tax).toFixed(2))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end border-b border-gray-100 pb-4 dark:border-gray-700">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Subtotal</span>
                <span>{currencyFormatter(order.subTotal)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-gray-100">
                <span>Grand Total</span>
                <span>{currencyFormatter(order.grandTotal)}</span>
              </div>
            </div>
          </div>

          <p className="pt-4 text-center text-xs text-gray-400 dark:text-gray-500">
            Purchase order — for receiving and records
          </p>
        </Card>

        {order.orderStatus === "pending" ? (
          <Card>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Receive goods</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Select a {warehouseLabel.toLowerCase()} to receive stock into. This will increase
              inventory for the selected {warehouseLabel.toLowerCase()}.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div className="w-64">
                <FilterSelect
                  variant="boxed"
                  label={warehouseLabel}
                  value={warehouseId}
                  onChange={setWarehouseId}
                  allLabel={`Select ${warehouseLabel.toLowerCase()}`}
                  options={warehouseOptions}
                  disabled={isWarehouseLocked}
                />
              </div>
              <Button
                onClick={handleReceiveGoods}
                disabled={!warehouseId || receive.loading}
                isLoading={receive.loading}
              >
                Receive goods
              </Button>
            </div>
          </Card>
        ) : order.orderStatus === "received" ? (
          <Card>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Received into{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {order.warehouseId?.name || warehouseLabel.toLowerCase()}
              </span>{" "}
              on {formatDateTime(order.receivedAt, "DD MMM YYYY, hh:mm A")}.
            </p>
          </Card>
        ) : null}
      </div>

      <div className="fixed left-[-9999px] top-0" aria-hidden>
        <PurchaseInvoiceDocument
          ref={invoiceRef}
          order={order}
          tenant={tenant}
          className="print-target"
        />
      </div>
    </DashboardLayout>
  );
};

export default PurchaseOrderDetailsPage;
